import { isSet } from '../utils/isSet';

class SiteSearch {
    constructor(root){
        this.root = root;
        this.searchField = root.querySelector('.site-search__field');
        this.searchInput = root.querySelector('input[type="search"]');
        this.resultsContainer = this.root.querySelector('.site-search__results');
        this.messagesContainer = this.root.querySelector('.site-search__messages');
        this.searchResultTemplate = document.querySelector('#search-result--template');

        this.messageTemplate = document.querySelector('#search-message--template');

        this.timeout = 0;
        this.previousSearchString = '';
        this.jsonURL = '';

        this.topResults = [];

        this.searchInput.addEventListener(
            'keyup',
            this.onInputKeyup.bind(this)
        );

        window.addEventListener(
            'load',
            this.init.bind(this),
            true
        );
    }

    /**
     * Checks if the passed string is different when compared with the previously stored search string this.previousSearchString
     * @param  {String}  value  The search query string that we want to check that has changed
     */
    hasChanged(value){
        if(value == this.previousSearchString){
            return false;
        }

        return true;
    }

    /**
     * Does some light string escaping
     * @param  {String}  string  The string that is to be escaped
     */
    escape(string) {
        return string
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");
    }
    
    isSet(node){
        if (typeof(node) != 'undefined' && node != null && node != "") {
            return true;
        }else{
            return false;
        }
    }

    /**
     * Initialise things here
     */
    init(){
        this.jsonURL = this.root.getAttribute('data-fetch-url');
    }

    /**
     * Fetches the results from JSON query
     */
    async fetchJSON(){
        const cleanValue = this.escape(this.searchInput.value);
        const searchURL = this.jsonURL + cleanValue;

        let responseJSON;

        try {
            const response = await fetch(searchURL);

            if (!response.ok) {
                // Handle response error
                throw new Error(`${response.status} ${response.statusText}`);
            }

            responseJSON = await response.json();
            
        } catch (error) {
            if (error instanceof SyntaxError) {
                console.warn('There was a SyntaxError when fetching the JSON', error);
            } else {
                console.warn('There was an error', error);
            }
        }

        if (responseJSON) {
            this.getTotalCounts(responseJSON);
            this.organiseJSON(responseJSON);
        }
    }

    /**
     * Counts the total items
     * @param {*} json JSON list of search results
     */
    getTotalCounts(json){
        this.totalCount = json.total;
    }

    /**
     * Gets and stores only the top JSON results
     * @param {*} json JSON list of search results
     */
    organiseJSON(json){
        const list = json.list;

        this.topResults = [];

        if(this.topResults.length === 10){
            this.buildResults();
            return;
        }

        for (let i = 0; i < list.length; i++) {
            let result = list[i];

            if(this.topResults.length === 10) break;

            this.topResults.push(result);
        }

        this.buildResults();
    }

    /**
     * Handles all keyup keyboard events
     * @param  {Object}  e  The event that triggered this method
     */
    onInputKeyup(e) {
        if(this.searchInput.value.length == 0 || this.searchInput.value == ''){
            this.removeResults();
            this.removeResultsFooter();
            this.clearMessages();
            return;
        }

        if(!this.hasChanged(this.searchInput.value)){
            return;
        }

        this.previousSearchString = this.searchInput.value;

        if(this.searchInput.value.length <= 3){
            this.clearMessages();
            this.removeResultsFooter();
            this.removeResults();

            this.messagesContainer.append(
                this.buildMessage('Please keep typing...', 'ma-icon--attention')
            )
            return;
        }

        clearTimeout(this.timeout);

        if(!isSet(this.resultsContainer.querySelector('.ma-icon--loading'))){
            this.clearMessages();
        }

        if(this.resultsContainer.innerHTML != ''){
            this.resultsContainer.classList.add('is-searching');
        } else{
            if(!isSet(this.resultsContainer.querySelector('.ma-icon--loading'))){
                this.messagesContainer.append(
                    this.buildMessage('Searching...', 'ma-icon--loading')
                );
            }
        }


        this.timeout = setTimeout(
            this.fetchJSON.bind(this),
            700
        );
    }


    /**
     * Begins the process of building the resulting HTML
     */
    buildResults(){
        this.clearMessages();
        this.resultsContainer.classList.remove('is-searching');

        if(this.topResults.length >= 1){
            this.removeResults();
            this.populateResults();
            this.buildResultsFooter();
        } else{
            this.removeResults();
            this.removeResultsFooter();

            this.messagesContainer.append(
                this.buildMessage('No results were found, please try a different search', 'ma-icon--attention')
            );
        }
    }


    /**
     * Populates and appends a results
     */
    populateResults(){
        for (let i = 0; i < this.topResults.length; i++) {
            let result = this.topResults[i];

            let searchResultElement = this.searchResultTemplate.content.cloneNode(true).children[0];

            if(
                !isSet(result.title) ||
                !isSet(result.href)
            ){
                return;
            }

            searchResultElement.querySelector('.search-result__title').textContent = result.title;
            searchResultElement.querySelector('.search-result__link').href = result.href;

            if(isSet(result.img)){
                searchResultElement.querySelector('.search-result__thumbnail').src = result.img;
            }

            if(isSet(result.excerpt)){
                searchResultElement.querySelector('.search-result__excerpt').textContent = result.excerpt;
            }

            if(isSet(result.type)){
                searchResultElement.querySelector('.search-result__type').textContent = result.type;
            }

            this.resultsContainer.appendChild(searchResultElement);
        }
    }

    /**
     * Removes the results
     */
    removeResults(){
        this.resultsContainer.innerHTML = '';
    }

    buildMessage(message, icon, classes){
        let messageElement = this.messageTemplate.content.cloneNode(true).children[0];

        if(isSet(message)) messageElement.querySelector('.search-message__p').innerHTML = message;
        
        if(isSet(icon)) messageElement.querySelector('.search-message__icon').classList.add(icon);

        if(isSet(classes)) messageElement.classList.add(classes);

        return messageElement;
    }

    clearMessages(){
        const messages = this.messagesContainer.querySelectorAll('.search-message');

        messages.forEach((message) => {
            message.remove();
        })
    }


    buildResultsFooter(){
        this.removeResultsFooter();

        const fragment = new DocumentFragment();

        const resultsFooter = document.createElement('div');
        resultsFooter.classList.add('layer__footer');

        const resultsFooterButton = document.createElement('a');
        resultsFooterButton.href = '/search/?q=' + this.escape(this.searchInput.value);
        resultsFooterButton.classList.add('btn', 'has-block-theme', 'has-block-theme--yellow');

        const resultsFooterButtonSpan = document.createElement('span');
        resultsFooterButtonSpan.textContent = 'View All Results (' + this.totalCount + ')';

        resultsFooterButton.append(resultsFooterButtonSpan);

        resultsFooter.append(resultsFooterButton);
        fragment.append(resultsFooter);

        this.root.append(fragment);
    }

    removeResultsFooter(){
        const resultsFooter = this.root.querySelector('.layer__footer');

        if(isSet(resultsFooter)) resultsFooter.remove();
    }
}

export { SiteSearch }